import React, { useState, useEffect } from "react";
import axios from "axios";
import CircularProgress from '@mui/joy/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useAuth } from "../../../context/AuthProvider";
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { GHOST_POSTS_GET_URL, BASE_URL, DEV_ADMIN_USERS } from "../../../constant/index";

const EmailUtility = () => {
    const { token } = useAuth();
    const [ posts, setPosts ] = useState([]);
    const [ selectedPost, setSelectedPost ] = useState(null);
    const [ isLoading, setLoading ] = useState(false);
	const [ errorMsg, setErrorMsg ] = useState('');
    const [ successMsg, setSuccessMsg ] = useState('');
    const [ isSendingToAll, setIsSendingToAll ] = useState(false);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const { data } = await axios.get(GHOST_POSTS_GET_URL);
                
                const tableData = data.posts.map((post) => ({
                    id: post.id,
                    post: post.title,
                    status: "Not Mailed",
                    html: post.html,
                }));
                setPosts(tableData || []);
            } catch (error) {
                console.error("Error fetching posts:", error);
            }
        };

        fetchPosts();
    }, []);

    if (!posts || posts.length === 0) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <p className="text-xl font-semibold">No posts available.</p>
            </div>
        );
    }

    const sendEmailActionToAll = async () => {
        const getPost = posts.find(p => p.id === selectedPost);
        if (!getPost) {
            setErrorMsg("Post not found");
            return;
        }
        try {
            setLoading(true);
            setErrorMsg('');
            await axios.post(BASE_URL + '/sendEmailToAll', {
                post: getPost?.html || '',
                title: getPost.post,
            });
            setSuccessMsg("Email sent successfully!");
        } catch (error) {
            setErrorMsg(error?.response?.data?.reason || "Failed to send email");
        } finally {
            setLoading(false);
            setIsSendingToAll(false);
            setSelectedPost(null);
        }
    }

    const sendEmailActionToDevs = async (postId) => {
        const selectedPost = posts.find(p => p.id === postId);
        if (!selectedPost) {
            setErrorMsg("Post not found");
            return;
        }
        const body = {
            post: selectedPost?.html || '',
            title: selectedPost.post,
            emails: DEV_ADMIN_USERS
        };
        try {
            setLoading(true);
            setErrorMsg('');
            await axios.post(BASE_URL + '/sendEmailToDevs', body);
            setSuccessMsg("Email sent successfully!");
            const updatePosts = posts.map((post) => {
                if (post.id === postId) {
                    return { ...post, status: "Mailed" };
                }
                return post;
            });
            setPosts(updatePosts);
        } catch (error) {
            setErrorMsg(error?.response?.data?.reason || "Failed to send email");
        } finally {
            setLoading(false);
        }
    }

    const handleSendToAll = (selectedPost) => {
        setIsSendingToAll(true);
        setSelectedPost(selectedPost);
    }

    const sendEmailActionToPendingUsers = async (postId) => {
        const selectedPost = posts.find(p => p.id === postId);
        if (!selectedPost) {
            setErrorMsg("Post not found");
            return;
        }
        const body = {
            post: selectedPost?.html || '',
            title: selectedPost.post,
        };
        try {
            setLoading(true);
            setErrorMsg('');
            await axios.post(BASE_URL + '/sendEmailToPendingUsers', body);
            setSuccessMsg("Email sent successfully!");
        } catch (error) {
            setErrorMsg(error?.response?.data?.reason || "Failed to send email");
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        { field: 'post', headerName: 'Posts', width: 500 },
        { field: 'status', headerName: 'Status', width: 200 },
        { 
            field: 'action', 
            headerName: 'Action', 
            width: 300,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <div className="d-flex justify-content-between align-items-center" style={{ cursor: "pointer" }}>
                            <div>
                                <Button size={'small'} variant="contained" onClick={() => handleSendToAll(params.row.id)}>Send To All</Button>
                            </div>
                            <div style={{ paddingLeft: '10px'}}>
                                <Button size={'small'} variant="contained" onClick={() => sendEmailActionToDevs(params.row.id)} disabled={isLoading}>Send To devs</Button>
                            </div>
                            <div style={{ paddingLeft: '10px'}}>
                                <Button size={'small'} variant="contained" onClick={() => sendEmailActionToPendingUsers(params.row.id)} disabled={isLoading}>Send To Pending users</Button>
                            </div>
                        </div>
                     </>
                );
            } 
        },
    ];

    const handleClose = () => {
        setIsSendingToAll(false);
    }

    return (
        <div className="container">
            {
                errorMsg && (
                    <Stack sx={{ width: '100%', marginTop: '10px' }} spacing={2}>
                        <Alert severity="error">{errorMsg}</Alert>
                    </Stack>
                )
            }
            {
                successMsg && (
                    <Stack sx={{ width: '100%', marginTop: '10px' }} spacing={2}>
                        <Alert severity="success">{successMsg}</Alert>
                    </Stack>
                )
            }
            {
                isLoading && (
                    <div className="max-w-6xl mx-auto p-6">
                        <CircularProgress />
                    </div>
                )
            }

            <div className="max-w-6xl mx-auto p-6">
                <div className="col-12">
                    <div style={{ height: 450, width: '100%', marginTop: '10px' }}>
                        <DataGrid
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            disableColumnMenu
                            rows={posts || []}
                            editMode="row"
                            columns={columns}
                            pageSizeOptions={[5, 50, 100]}
                            localeText={{ noRowsLabel: "No posts found" }}
                            slots={{ toolbar: () => <GridToolbarQuickFilter /> }}
                            hideFooterSelectedRowCount={true}
                        />
                    </div>
                </div>
            </div>
            {
                isSendingToAll && (
                    <Dialog
                        open={isSendingToAll}
                        onClose={handleClose}
                        maxWidth='sm'
                        fullWidth={true}
                    >
                        <DialogContent>
                            <DialogContentText component={'div'}>
                                Are you sure you want to send this email to all users? This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={sendEmailActionToAll}>Yes, please go ahead!</Button>
                        </DialogActions>
                    </Dialog>
                )
            }
       </div>
    )
}

export default EmailUtility;